// frontend/src/utils/parseApiError.js
//
// What a failed request throws — `ApiError` for an answer the server refused
// with, `NetworkError` for a request that never got one — and the conversion
// of a DRF-style error response body into human-readable lines.
//
// Handles the shapes DRF emits for ValidationError:
//   "message"                                         → ["message"]
//   ["m1", "m2"]                                      → ["m1", "m2"]
//   { detail: "message" }                             → ["message"]
//   { detail: ["m1", "m2"] }                          → ["m1", "m2"]
//   { non_field_errors: ["..."] }                     → ["..."]
//   { field1: ["e1"], field2: ["e2"] }                → ["field1: e1", "field2: e2"]
//   { field: { nested: ["..."] } }                    → ["field: nested: ..."]
//   null / undefined                                  → []
//
// Also tolerates Python-repr strings that occasionally arrive from
// imperative error paths (e.g. "{'field': ['msg']}") — parsed via a
// quote-replace best effort, then fed back through.
//
// Callers that need to extract domain-specific structured payloads
// (e.g. promotion `conflicts` array) should destructure those fields
// BEFORE passing the residual body to these helpers — the util is for
// generic DRF error text, not for rendering arbitrary shapes.
//
// `apiErrorFrom(response)` is the one place a REFUSED response's body is
// read; every `if (!res.ok)` branch in the app throws or inspects the
// `ApiError` it builds. This module has no imports, so both the session
// service and the query layer can use it without a cycle.

const FIELD_PREFIX_SKIP = new Set(['detail', 'non_field_errors']);

export function flattenApiError(err) {
  if (err == null) return [];

  if (typeof err === 'string') {
    const trimmed = err.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return flattenApiError(JSON.parse(trimmed.replace(/'/g, '"')));
      } catch {
        // Not valid JSON — fall through and treat as plain string.
      }
    }
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(err)) {
    return err.flatMap(flattenApiError);
  }

  if (typeof err === 'object') {
    const keys = Object.keys(err);
    // Unwrap `detail` / `non_field_errors` when they're the only key —
    // those are DRF conventions for non-field-level messages and shouldn't
    // render with a field prefix.
    if (keys.length === 1 && FIELD_PREFIX_SKIP.has(keys[0])) {
      return flattenApiError(err[keys[0]]);
    }
    return keys.flatMap((field) => {
      const lines = flattenApiError(err[field]);
      if (FIELD_PREFIX_SKIP.has(field)) return lines;
      return lines.map((m) => `${field}: ${m}`);
    });
  }

  return [String(err)];
}

// Return the backend's structured error code from a response body, or null.
//
// Manexys has TWO paths for structured errors and they emit different
// wire shapes. Both are accepted here:
//
//   1. `DomainError` raised from DRF views → `{error: '<code>',
//      message: '<dev>'}` at the top level. DRF's exception handler
//      unwraps dict-typed details rather than nesting under 'detail'.
//   2. `structured_json_error()` from function-based Django views →
//      `{detail: {error: '<code>', message: '<dev>'}}`, nested. Used
//      by views that don't pass through the DRF dispatcher
//      (billing/views/saas.py).
//
// Read both — top-level first since that's the more common path.
// Returns null when neither shape carries a string code.
//
// Callers should branch on whether a code is present — translate to
// `apiErrors.<code>` when it is, otherwise fall back to a caller-chosen
// generic message key (e.g. `msg.saveFailed`).
export function extractStructuredErrorCode(body) {
  const code = body?.error ?? body?.detail?.error;
  return typeof code === 'string' ? code : null;
}

/**
 * Typed error for a refused response. Consumers branch on:
 *   - `.code`   → for `t(\`apiErrors.\${code}\`)` translation
 *   - `.body`   → for structured payloads (promotion conflicts, per-field
 *                  settings errors, values a translation interpolates)
 *   - `.status` → for HTTP-level branching (404 → "not found" UX)
 *
 * `.message` is dev-facing only (per CLAUDE.md) — never display directly.
 */
export class ApiError extends Error {
  constructor({ status, code, body, message }) {
    super(message || `API ${status}: ${code || 'error'}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code ?? null;
    this.body = body ?? null;
  }
}

/**
 * A request that never reached the server — the browser could not complete the
 * fetch (offline, DNS, a dropped connection, a TLS failure). There is no
 * status, because no answer came back. `queries/client.js` retries this once
 * and nothing else: a server that answered has said what it means.
 */
export class NetworkError extends Error {
  constructor(context, cause) {
    super(`Request did not reach the server: ${context}`, { cause });
    this.name = 'NetworkError';
  }
}

/**
 * The `ApiError` for a refused (non-2xx) response — the one read of a
 * refused body.
 *
 * The status already states the failure; the body is the optional detail on
 * it. DRF and both DomainError paths always send JSON, but a gateway error
 * page (an Azure 502 during a deploy) or a crashed non-DRF view sends HTML or
 * nothing. That case is logged here, once, and the error still carries the
 * status with `code` and `body` null — no caller repeats the log, and none
 * gets a guessed body.
 *
 * `context` names the operation ("POST /api/x/") in the log line and the
 * dev-facing message; omit it where the caller's own log names it.
 */
export async function apiErrorFrom(response, context) {
  const where = context ? ` on ${context}` : '';
  let body = null;
  try {
    body = await response.json();
  } catch (err) {
    console.error(
      `Refused response ${response.status}${where} carried no JSON body:`, err.message,
    );
  }
  return new ApiError({
    status: response.status,
    code: extractStructuredErrorCode(body),
    body,
    message: body?.message || body?.detail?.message || formatApiError(body)
      || `HTTP ${response.status}${where}`,
  });
}

export function formatApiError(err, { maxLength = 500, separator = ' · ' } = {}) {
  const lines = flattenApiError(err);
  if (lines.length === 0) return '';
  const joined = lines.join(separator);
  if (joined.length <= maxLength) return joined;
  return `${joined.slice(0, maxLength - 1)}…`;
}
